import JSZip from 'jszip'
import { optimizeSvg } from '#app/utils/svg-optimizer.ts'

// Extend File interface to include webkitRelativePath
declare global {
	interface File {
		readonly webkitRelativePath: string
	}
}

export type ProcessedIcon = {
	name: string
	content: string
	optimizedContent: string
	path: string
}

function hasHiddenPathSegment(path: string): boolean {
	return path.split('/').some((segment) => segment.startsWith('.'))
}

function getIconPath(filePath: string): string {
	const pathParts = filePath.split('/')
	if (pathParts.length === 1) {
		return pathParts[0] || ''
	}
	return pathParts.slice(0, -1).join('/')
}

function getIconName(filePath: string): string {
	const pathParts = filePath.split('/')
	return pathParts[pathParts.length - 1] || ''
}

export async function processFiles(
	files: File[] | FileList,
): Promise<ProcessedIcon[]> {
	const fileArray = Array.from(files)
	const allIcons: ProcessedIcon[] = []
	const errors: string[] = []

	for (const file of fileArray) {
		try {
			if (file.name.endsWith('.zip')) {
				const zipIcons = await processZipFile(file)
				allIcons.push(...zipIcons)
			} else if (file.name.endsWith('.svg')) {
				const svgIcon = await processSvgFile(file)
				if (svgIcon) allIcons.push(svgIcon)
			}
		} catch (error) {
			errors.push(`Failed to process ${file.name}: ${error}`)
		}
	}

	// If there were errors, throw them so the caller can handle them
	if (errors.length > 0) {
		throw new Error(errors.join('; '))
	}

	return allIcons
}

async function processZipFile(file: File): Promise<ProcessedIcon[]> {
	const icons: ProcessedIcon[] = []
	const jszip = new JSZip()

	const zip = await jszip.loadAsync(file)

	const iconFiles = Object.values(zip.files).filter(
		(file) =>
			!file.dir &&
			file.name.endsWith('.svg') &&
			!hasHiddenPathSegment(file.name),
	)

	for (const iconFile of iconFiles) {
		try {
			const svg = await iconFile.async('text')

			if (svg.length > 20000) {
				console.warn(`${iconFile.name} is large (${svg.length / 1000} KB)`)
			}

			const filePath = iconFile.name.replace('.svg', '')
			icons.push({
				path: getIconPath(filePath),
				name: getIconName(filePath),
				content: svg,
				optimizedContent: optimizeSvg(svg),
			})
		} catch (error) {
			console.error(`Failed to parse ${iconFile.name}:`, error)
		}
	}

	return icons
}

async function processSvgFile(file: File): Promise<ProcessedIcon | null> {
	// Skip files in hidden directories
	if (
		file.webkitRelativePath &&
		hasHiddenPathSegment(file.webkitRelativePath)
	) {
		console.info(`Skipped hidden file: ${file.webkitRelativePath}`)
		return null
	}

	const content = await file.text()
	const name = file.name.replace('.svg', '')

	// For individual SVG file uploads, always place at root level (empty path)
	// Only preserve directory structure for actual directory uploads
	let filePath = ''
	if (file.webkitRelativePath && file.webkitRelativePath !== file.name) {
		// This is a directory upload, preserve the directory structure
		const fullPath = file.webkitRelativePath.replace('.svg', '')
		const pathParts = fullPath.split('/')
		if (pathParts.length > 1) {
			// Keep the full path except for the file name itself
			filePath = pathParts.slice(0, -1).join('/')
		}
	}

	return {
		name,
		content,
		optimizedContent: optimizeSvg(content),
		path: filePath,
	}
}

type TreeNode = {
	name: string
	id: string
	iconsCount: number
	childItems: TreeNode[]
	icon?: ProcessedIcon // For individual icon items
	isIcon?: boolean // True if this is an individual icon item
}

/**
 * Normalizes icon paths by removing unnecessary single-level parent folders
 * Similar to web app's getNormalizedIconPaths function
 */
export function normalizeIconPaths(icons: ProcessedIcon[]): ProcessedIcon[] {
	if (icons.length === 0) return icons

	let normalizedIcons = [...icons]
	const allPaths = icons.map((icon) => icon.path).filter(Boolean)

	if (allPaths.length === 0) return normalizedIcons

	let arrayOfPaths = allPaths.map((path) => path.split('/').filter(Boolean))

	// Keep removing common parents until we find a level where not all paths share the same parent
	let hasCommonParent = true
	while (
		hasCommonParent &&
		arrayOfPaths.length > 0 &&
		arrayOfPaths[0] &&
		arrayOfPaths[0].length > 0
	) {
		const possibleCommonParent = arrayOfPaths[0][0]

		// Check if all paths share this common parent
		if (
			possibleCommonParent &&
			arrayOfPaths.every(
				(pathArray) =>
					pathArray.length > 0 && pathArray[0] === possibleCommonParent,
			)
		) {
			// Remove this common parent from all icon paths
			normalizedIcons = normalizedIcons.map((icon) => {
				if (!icon.path) return icon

				const pathParts = icon.path.split('/').filter(Boolean)
				if (pathParts.length > 0 && pathParts[0] === possibleCommonParent) {
					return {
						...icon,
						path: pathParts.slice(1).join('/'),
					}
				}
				return icon
			})

			// Remove first segment from path arrays for next iteration
			arrayOfPaths = arrayOfPaths.map((pathArray) => pathArray.slice(1))
		} else {
			hasCommonParent = false
		}
	}

	return normalizedIcons
}

export function createTreeStructure(icons: ProcessedIcon[]): TreeNode[] {
	const root: TreeNode[] = []
	const pathToNode = new Map<string, TreeNode>()

	// Group icons by their paths
	const iconsByPath = new Map<string, ProcessedIcon[]>()
	icons.forEach((icon) => {
		const path = icon.path
		let pathIcons = iconsByPath.get(path)
		if (!pathIcons) {
			pathIcons = []
			iconsByPath.set(path, pathIcons)
		}
		pathIcons.push(icon)
	})

	// Get all unique paths and sort them
	const allPaths = Array.from(iconsByPath.keys()).sort()

	allPaths.forEach((path) => {
		const pathParts = path.split('/').filter(Boolean)
		let currentLevel = root
		let currentPath = ''

		// Create directory structure
		pathParts.forEach((part) => {
			currentPath = currentPath ? `${currentPath}/${part}` : part
			let node = pathToNode.get(currentPath)

			if (!node) {
				// Count icons that start with this path
				const iconsCount = icons.filter((icon) =>
					icon.path.startsWith(currentPath),
				).length

				node = {
					name: part,
					id: currentPath,
					iconsCount,
					childItems: [],
				}

				pathToNode.set(currentPath, node)
				currentLevel.push(node)
			}

			currentLevel = node.childItems
		})

		// Add individual icons as leaf nodes
		const iconsInPath = iconsByPath.get(path) || []

		// Handle empty path icons (root-level icons)
		if (path === '') {
			// Add icons directly to the root level
			iconsInPath.forEach((icon) => {
				const iconNode: TreeNode = {
					name: icon.name,
					id: icon.name,
					iconsCount: 1,
					childItems: [],
					icon,
					isIcon: true,
				}
				root.push(iconNode)
			})
		} else {
			// Handle icons in subdirectories
			const parentNode = pathToNode.get(path)
			if (parentNode) {
				iconsInPath.forEach((icon) => {
					const iconNode: TreeNode = {
						name: icon.name,
						id: `${path}/${icon.name}`,
						iconsCount: 1,
						childItems: [],
						icon,
						isIcon: true,
					}
					parentNode.childItems.push(iconNode)
				})
			}
		}
	})

	// Sort root level items: folders first, then icons
	root.sort((a, b) => {
		// If one is a folder and other is an icon, folder comes first
		if (!a.isIcon && b.isIcon) return -1
		if (a.isIcon && !b.isIcon) return 1
		// If both are same type, sort alphabetically by name
		return a.name.localeCompare(b.name)
	})

	return root
}

export function getSelectedIcons(
	selectedKeys: Set<string>,
	tree: TreeNode[],
): ProcessedIcon[] {
	const selectedIcons: ProcessedIcon[] = []

	const collectIcons = (nodes: TreeNode[]) => {
		nodes.forEach((node) => {
			if (selectedKeys.has(node.id)) {
				if (node.isIcon && node.icon) {
					// Individual icon is selected
					selectedIcons.push(node.icon)
				} else if (!node.isIcon) {
					// Directory is selected, collect all icons within it
					const collectAllIcons = (childNodes: TreeNode[]) => {
						childNodes.forEach((childNode) => {
							if (childNode.isIcon && childNode.icon) {
								selectedIcons.push(childNode.icon)
							} else {
								collectAllIcons(childNode.childItems)
							}
						})
					}
					collectAllIcons(node.childItems)
				}
			} else {
				// Check children even if parent isn't selected
				collectIcons(node.childItems)
			}
		})
	}

	collectIcons(tree)
	return selectedIcons
}

export function countSelectedIcons(
	selectedKeys: Set<string>,
	tree: TreeNode[],
): number {
	let iconCount = 0

	const countIcons = (nodes: TreeNode[]) => {
		nodes.forEach((node) => {
			if (selectedKeys.has(node.id)) {
				if (node.isIcon && node.icon) {
					// Individual icon is selected
					iconCount++
				} else if (!node.isIcon) {
					// Directory is selected, count all icons within it
					const countAllIcons = (childNodes: TreeNode[]) => {
						childNodes.forEach((childNode) => {
							if (childNode.isIcon && childNode.icon) {
								iconCount++
							} else {
								countAllIcons(childNode.childItems)
							}
						})
					}
					countAllIcons(node.childItems)
				}
			} else {
				// Check children even if parent isn't selected
				countIcons(node.childItems)
			}
		})
	}

	countIcons(tree)
	return iconCount
}
